// All STAC API calls in one place. Every path is relative — CRA proxy routes to :8001.

// ── Shared domain types ───────────────────────────────────────────────────────

export interface StateNexusData {
  stateCode: string;
  stateName: string;
  nexusTriggered: boolean;
  revenueThresholdPct: number;
  revenue: number;
  revenueThreshold: number;
  directRevenue: number;
  mfRevenue: number;
  mfExcluded: boolean;
  transactionCount: number;
  transactionThreshold: number | null;
  registrationStatus: string;
  filingStatus: string;
}

export interface NexusAlert {
  id: string;
  stateCode: string;
  stateName: string;
  alertLevel: 80 | 90 | 100;
  triggeredAt: string;
  dismissed: boolean;
}

export interface RateLookupJurisdiction {
  city: string;
  county: string;
  combined_rate: number;
}

export interface RateLookupResult {
  postal_code: string;
  city: string;
  state_code: string;
  county: string;
  combined_rate: number;
  state_rate: number;
  county_rate: number;
  city_rate: number;
  special_district_rate: number;
  origin_destination: 'O' | 'D';
  taxable_services: boolean;
  taxable_freight: boolean;
  all_jurisdictions: RateLookupJurisdiction[];
}

export interface TaxCategory {
  tic_code: string;
  tic_name: string;
  description: string | null;
}

export interface TaxabilityInfo {
  status: 'taxable' | 'exempt' | 'reduced';
  rate_modifier?: number;
}

export type TaxabilityResponse = Record<string, TaxabilityInfo>;

export interface Product {
  product_id: string;
  channel: string;
  channel_product_id: string;
  product_name: string;
  product_type: string | null;
  current_tic_code: string | null;
  confidence_score: number | null;
  classification_method: 'auto' | 'override' | 'pending_review' | 'manual';
  suggested_tic_code: string | null;
  suggested_tic_name: string | null;
  estimated_revenue_impact: number | null;
}

export interface ClassificationCounts {
  total: number;
  pending: number;
  auto: number;
  override: number;
}

export interface ClassificationQueueResponse {
  items: Product[];
  total: number;
  page: number;
  counts: ClassificationCounts;
}

export interface JurisdictionItem {
  jurisdiction_level: 'state' | 'county' | 'city' | 'special_district';
  jurisdiction_name: string;
  taxable_amount: number;
  rate: number;
  tax_amount: number;
}

export interface LiabilityStateRow {
  state_code: string;
  state_name: string;
  period: string;
  total_liability: number;
  status: 'filed' | 'due' | 'overdue';
  due_date: string | null;
  days_until_due: number | null;
  filing_frequency: string | null;
  filing_generated_at: string | null;
  qbo_synced_at: string | null;
}

export interface LiabilitySummary {
  period: string | null;
  total_liability: number;
  states: LiabilityStateRow[];
}

export interface FilingData {
  filing_id: string;
  state_code: string;
  state_name: string;
  period: string;
  period_start: string;
  period_end: string;
  status: 'pending_review' | 'approved' | 'filed';
  gross_sales: number;
  exempt_sales: number;
  taxable_sales: number;
  tax_collected: number;
  tax_due: number;
  approved_at: string | null;
  approved_by: string | null;
  jurisdictions: JurisdictionItem[];
}

export interface FilingTransaction {
  transaction_id: string;
  transaction_date: string;
  order_id: string;
  channel: string;
  product_name: string;
  gross_amount: number;
  exempt_amount: number;
  taxable_amount: number;
  tax_rate: number;
  tax_amount: number;
}

export interface TransactionPage {
  total: number;
  page: number;
  pages: number;
  items: FilingTransaction[];
}

export interface CreateTransactionPayload {
  platform: string;
  platform_order_id: string;
  transaction_date: string;
  state_code: string;
  gross_amount: number;
  tax_collected: number;
  is_marketplace: boolean;
  product_id?: string;
  tic_code?: string;
}

export interface OrgTransaction {
  id: string;
  organization_id: string;
  platform: string;
  platform_order_id: string;
  transaction_date: string;
  state_code: string;
  gross_amount: number;
  tax_collected: number;
  is_marketplace: boolean;
  product_id: string | null;
  tic_code: string | null;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

interface ErrorBody {
  error?: string;
  detail?: string;
}

// Carries the HTTP status alongside the message so callers can distinguish
// "forbidden" (403 — authenticated but not permitted, retrying won't help)
// from other failures (5xx/network — retrying might).
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

let authToken: string | null = null;

// Called from AuthContext once a PropelAuth session is resolved, so every
// request here carries the token apps/stac's authenticate() middleware expects.
export function setAuthToken(token: string | null): void {
  authToken = token;
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    ...options,
  });
  if (!res.ok) {
    const err = (await res.json().catch((): ErrorBody => ({}))) as ErrorBody;
    throw new ApiError(err.error ?? err.detail ?? `HTTP ${res.status}`, res.status);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// ── Nav badge counts ──────────────────────────────────────────────────────────

interface CountResponse {
  count: number;
}

export const stacCountsApi = {
  nexusAlertsCount: (organizationId: string): Promise<CountResponse> =>
    request(`/api/v1/nexus/organizations/${organizationId}/nexus-alerts/count`),

  classificationQueueCount: (organizationId: string): Promise<CountResponse> =>
    request(`/api/v1/organizations/${organizationId}/classification-queue/count`),
};

// ── Nexus Status ──────────────────────────────────────────────────────────────

interface AlertsResponse {
  alerts: NexusAlert[];
  total: number;
}
interface DismissResponse {
  success: boolean;
}

export const nexusStatusApi = {
  getStatus: (organizationId: string): Promise<StateNexusData[]> =>
    request(`/api/v1/nexus/organizations/${organizationId}/nexus-status`),

  getAlerts: (organizationId: string): Promise<AlertsResponse> =>
    request(`/api/v1/nexus/organizations/${organizationId}/nexus-alerts`),

  dismissAlert: (organizationId: string, alertId: string): Promise<DismissResponse> =>
    request(`/api/v1/nexus/organizations/${organizationId}/nexus-alerts/${alertId}/dismiss`, {
      method: 'POST',
    }),
};

// ── Rate lookup ───────────────────────────────────────────────────────────────

export const rateLookupApi = {
  lookup: (postalCode: string, organizationId: string): Promise<RateLookupResult> =>
    request(
      `/api/v1/rates/lookup?postal_code=${encodeURIComponent(postalCode)}&organization_id=${organizationId}`,
    ),
};

// ── Agent chat ────────────────────────────────────────────────────────────────

interface AgentResponse {
  answer: string;
}

export const agentApi = {
  chat: (question: string, organizationId: string): Promise<AgentResponse> =>
    request('/api/v1/agent/chat', {
      method: 'POST',
      body: JSON.stringify({ question, organization_id: organizationId }),
    }),
};

// ── Classification ────────────────────────────────────────────────────────────

interface ClassificationQueryParams {
  page?: number;
  pageSize?: number;
  filter?: string;
  search?: string;
}

interface ClassifyResponse {
  success: boolean;
  product_id: string;
  new_tic_code: string;
}
interface BulkClassifyResponse {
  success: boolean;
  updated_count: number;
}

export const classificationApi = {
  getQueue: (
    organizationId: string,
    { page = 1, pageSize = 50, filter = 'all', search = '' }: ClassificationQueryParams = {},
  ): Promise<ClassificationQueueResponse> =>
    request(
      `/api/v1/organizations/${organizationId}/classification-queue?page=${page}&page_size=${pageSize}&filter_by=${filter}&search=${encodeURIComponent(search)}`,
    ),

  getTaxCategories: (organizationId: string): Promise<TaxCategory[]> =>
    request(`/api/v1/organizations/${organizationId}/tax-categories`),

  getTopStates: (organizationId: string): Promise<string[]> =>
    request(`/api/v1/organizations/${organizationId}/top-states`),

  getTaxability: (ticCode: string, states: string[]): Promise<TaxabilityResponse> =>
    request(`/api/v1/tax-categories/${ticCode}/taxability?states=${states.join(',')}`),

  classify: (
    productId: string,
    organizationId: string,
    ticCode: string,
  ): Promise<ClassifyResponse> =>
    request(`/api/v1/products/${productId}/classify`, {
      method: 'POST',
      body: JSON.stringify({ tic_code: ticCode, organization_id: organizationId }),
    }),

  classifyBulk: (
    organizationId: string,
    productIds: string[],
    ticCode: string,
  ): Promise<BulkClassifyResponse> =>
    request(`/api/v1/organizations/${organizationId}/classify-bulk`, {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, tic_code: ticCode }),
    }),
};

// ── Tax Liability ─────────────────────────────────────────────────────────────

export const liabilityApi = {
  getSummary: (organizationId: string): Promise<LiabilitySummary> =>
    request(`/api/v1/organizations/${organizationId}/liability-summary`),

  getJurisdictions: (organizationId: string, stateCode: string): Promise<JurisdictionItem[]> =>
    request(`/api/v1/organizations/${organizationId}/liability/${stateCode}/jurisdictions`),
};

// ── Filing Review ─────────────────────────────────────────────────────────────

export interface FilingHistoryItem {
  filing_id: string;
  state_code: string;
  state_name: string;
  period: string;
  period_start: string | null;
  period_end: string | null;
  frequency: string | null;
  status: 'pending' | 'ready' | 'approved' | 'downloaded' | 'filed';
  tax_due: number | null;
  generated_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  amendments_filing_id: string | null;
  is_amendment: boolean;
}

export interface FilingHistoryPage {
  items: FilingHistoryItem[];
  total: number;
  pages: number;
}

export interface AmendmentRequest {
  reason: string;
  corrected_tic_code?: string | null;
}
export interface AmendmentResponse {
  success: boolean;
  amendment_id: string;
}

interface ApproveResponse {
  success: boolean;
  approved_at: string;
  approved_by: string;
}

export interface ValidationCheck {
  check: string;
  passed: boolean;
  error?: string;
  details?: unknown;
}
export interface ValidationResult {
  valid: boolean;
  ser_applicable: boolean;
  checks: ValidationCheck[];
}

export const filingApi = {
  getReview: (organizationId: string, stateCode: string, period: string): Promise<FilingData> =>
    request(
      `/api/v1/organizations/${organizationId}/filings/${stateCode}/review?period=${encodeURIComponent(period)}`,
    ),

  getTransactions: (
    organizationId: string,
    stateCode: string,
    period: string,
    page = 1,
    filter = 'gross_sales',
  ): Promise<TransactionPage> => {
    const params = new URLSearchParams({ period, page: String(page), limit: '50', filter });
    return request(
      `/api/v1/organizations/${organizationId}/filings/${stateCode}/transactions?${params}`,
    );
  },

  approve: (organizationId: string, stateCode: string, period: string): Promise<ApproveResponse> =>
    request(`/api/v1/organizations/${organizationId}/filings/${stateCode}/approve`, {
      method: 'POST',
      body: JSON.stringify({ period }),
    }),

  validate: (organizationId: string, filingId: string): Promise<ValidationResult> =>
    request(`/api/v1/organizations/${organizationId}/filings/${filingId}/validate`, {
      method: 'POST',
    }),

  // Anchor-href downloads can't carry the Authorization header, and the CRA dev
  // proxy only forwards non-text/html requests — so downloads must go through
  // fetch and be saved from a blob.
  downloadFiling: async (
    organizationId: string,
    filingId: string,
    format: string,
  ): Promise<void> => {
    const res = await fetch(
      `/api/v1/organizations/${organizationId}/filings/${filingId}/download/${format}`,
      { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
    );
    if (!res.ok) {
      const err = (await res.json().catch((): ErrorBody => ({}))) as ErrorBody;
      throw new ApiError(err.error ?? err.detail ?? `HTTP ${res.status}`, res.status);
    }
    const filename =
      res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
      `filing.${format}`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  getHistory: (organizationId: string, page = 1): Promise<FilingHistoryPage> =>
    request(`/api/v1/organizations/${organizationId}/filings/history?page=${page}&limit=25`),

  flagTransaction: (
    organizationId: string,
    transactionId: string,
    req: AmendmentRequest,
  ): Promise<AmendmentResponse> =>
    request(
      `/api/v1/organizations/${organizationId}/transactions/${transactionId}/flag-amendment`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      },
    ),
};

// ── Transactions ──────────────────────────────────────────────────────────────

interface ListTransactionsOptions {
  stateCode?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
}

interface TaxLinesResponse {
  lines: OrgTransaction[];
}

export const transactionsApi = {
  create: (organizationId: string, payload: CreateTransactionPayload): Promise<OrgTransaction> =>
    request(`/api/v1/organizations/${organizationId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  list: (
    organizationId: string,
    { stateCode, fromDate, toDate, limit = 50 }: ListTransactionsOptions = {},
  ): Promise<OrgTransaction[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (stateCode) params.set('state_code', stateCode);
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    return request(`/api/v1/organizations/${organizationId}/transactions?${params}`);
  },

  calculateTax: (organizationId: string, transactionId: string): Promise<OrgTransaction> =>
    request(`/api/v1/organizations/${organizationId}/transactions/${transactionId}/calculate-tax`, {
      method: 'POST',
    }),

  getTaxLines: (organizationId: string, transactionId: string): Promise<TaxLinesResponse> =>
    request(`/api/v1/organizations/${organizationId}/transactions/${transactionId}/tax-lines`),
};
